import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../App";
import { API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle, XCircle, Loader2, Crown, Calendar } from "lucide-react";
import { Button } from "../components/ui/button";

const MembershipSuccessPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { refreshUser } = useAuth();
  const [status, setStatus] = useState("verifying");
  const [expiryDate, setExpiryDate] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    const verifyPayment = async () => {
      const accessCode = searchParams.get("AccessCode");
      if (!accessCode) {
        setStatus("error");
        setErrorMessage("Missing payment reference.");
        return;
      }

      try {
        const response = await axios.post(`${API}/membership/verify`, {
          access_code: accessCode,
        });
        if (response.data.success) {
          setStatus("success");
          setExpiryDate(response.data.subscription_expiry);
          if (refreshUser) await refreshUser();
          toast.success("Membership activated!");
        } else {
          setStatus("failed");
          setErrorMessage(response.data.message || "Payment declined.");
        }
      } catch (error) {
        setStatus("error");
        setErrorMessage(error.response?.data?.detail || "Unable to verify payment.");
      }
    };
    verifyPayment();
  }, [searchParams, refreshUser]);

  const formatDate = (isoDate) => {
    if (!isoDate) return "";
    return new Date(isoDate).toLocaleDateString("en-AU", {
      day: "numeric", month: "long", year: "numeric",
    });
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center" data-testid="membership-success-page">
      <div className="bg-white rounded-xl shadow-sm p-8 max-w-md w-full mx-4 text-center">
        {status === "verifying" && (
          <div data-testid="membership-verifying">
            <Loader2 className="w-12 h-12 text-sky-600 animate-spin mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-900 mb-2">Verifying Payment</h2>
            <p className="text-slate-600">Setting up your membership...</p>
          </div>
        )}

        {status === "success" && (
          <div data-testid="membership-success">
            <div className="w-20 h-20 bg-sky-100 rounded-full flex items-center justify-center mx-auto mb-5">
              <Crown className="w-10 h-10 text-sky-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2">
              You're in! Welcome to BundleCart.
            </h2>
            <p className="text-slate-600 mb-6 leading-relaxed">
              Your account is now upgraded. You will now automatically receive the{" "}
              <span className="font-semibold text-sky-700">4% Member Convenience Fee</span> on every shop (capped at $15).
            </p>
            <div className="bg-sky-50 rounded-lg p-4 mb-6 flex items-center justify-center gap-2">
              <Calendar className="w-4 h-4 text-sky-600" />
              <span className="text-sm text-sky-800">
                Your next renewal is on <strong>{formatDate(expiryDate)}</strong>
              </span>
            </div>
            <div className="space-y-3">
              <Button className="w-full btn-primary h-11" onClick={() => navigate("/products")} data-testid="start-shopping-btn">
                Start Shopping
              </Button>
              <Button variant="outline" className="w-full" onClick={() => navigate("/account")} data-testid="view-account-btn">
                View Account
              </Button>
            </div>
          </div>
        )}

        {(status === "failed" || status === "error") && (
          <div data-testid="membership-failed">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <XCircle className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="text-xl font-semibold text-slate-900 mb-2">Payment Failed</h2>
            <p className="text-slate-600 mb-6">{errorMessage}</p>
            <Button className="w-full btn-accent" onClick={() => navigate("/")} data-testid="go-home-btn">Go Home</Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default MembershipSuccessPage;
